/**
 * What the reader is allowed to see of the model's thinking.
 *
 * The decision (2026-09-08) was to keep showing the chain of thought — it is
 * genuinely useful to watch an engineering problem being worked through — while
 * keeping our own prompt out of it. A thinking model quotes its instructions
 * back at itself, so the panel filled up with the scaffolding we had handed it:
 * the eight-way topology enum numbered 1..8, the schema's field names, the
 * headings `retrieveContext` builds ("Safety priorities: ..."), and the line
 * telling it to go and write the JSON. None of that is reasoning about the
 * user's machine, and all of it reads as the product talking to itself.
 *
 * Whole lines only, and only lines that are recognisably ours. A sentence that
 * merely mentions a conveyor or a timeout is the reasoning we want to keep, so
 * nothing here matches mid-sentence and nothing rewrites what it keeps.
 *
 * Display only. The stored copy keeps everything — when a turn has to be
 * explained after the fact, the scaffolding is part of the evidence.
 */

/** Field names from the requirement-analysis schema, as the prompt spells them. */
const SCHEMA_FIELD_NAMES = [
  'programName',
  'programs',
  'topology',
  'logicRules',
  'safetyConstraints',
  'ioSignals',
  'operatingModes',
  'controlStrategy',
  'taskType',
  'desiredOutputs',
  'keyConditions',
  'inferredSequenceSteps',
  'missingInformation',
  'blockerPriority',
  'fieldKeys',
  'valueMs',
  'timings',
];

/** The topology enum, which the prompt presents as a numbered list. */
const TOPOLOGY_ENUM = [
  'conveyor',
  'sequence',
  'tank',
  'alarm_manager',
  'pid_loop',
  'motor_with_feedback',
  'single_motor',
  'general_logic',
];

/** `1. conveyor — ...` and friends: our few-shot list read back to us. */
const ENUM_LIST_LINE = new RegExp(`^\\s*\\d+[.)、]\\s*(${TOPOLOGY_ENUM.join('|')})\\b`, 'i');

/** "Now write detailed JSON.", "输出严格的 JSON", "按 schema 生成 JSON" — instructions, not thinking. */
const JSON_INSTRUCTION_LINE =
  /^[^.。]{0,60}\b(?:now\s+)?(?:write|output|produce|emit|generate|return)\b[^.。]{0,40}\bjson\b/i;
const JSON_INSTRUCTION_LINE_ZH = /^[^。]{0,30}(?:输出|生成|返回|按照?)[^。]{0,20}JSON/i;

/** Headings retrieveContext.ts assembles into the knowledge block. */
const CONTEXT_HEADING_LINE = /^\s*(?:safety priorities|vendor rules|i\/o table|memory notes|additional context)\s*[:：]/i;

/** A bare schema key on a line of its own, with or without quotes. */
const BARE_SCHEMA_KEY_LINE = new RegExp(`^\\s*["'\`]?(?:${SCHEMA_FIELD_NAMES.join('|')})["'\`]?\\s*[:：]?\\s*$`);

function countSchemaFieldNames(line: string): number {
  return SCHEMA_FIELD_NAMES.filter(name => line.includes(name)).length;
}

/**
 * The analysis prompt narrated back to the reader.
 *
 * A thinking model plans its own formatting out loud, and the requirement
 * analysis prompt gives it a three-step shape to plan against — so the panel
 * filled with 「第一步是开头的自然确认，放在 [START_ECHO] 和 [END_ECHO] 之间」、
 * 「第二步是emoji分区分析」、「按严格结构：第一步开头一句话 → 第二步分区分析」.
 * None of that is thinking about the machine.
 *
 * Our markers are unambiguous, so any line naming one goes. The step words go
 * only when they appear beside a word about *form* — a sequence in the plant is
 * numbered too, and 「顺控第一步是进料阀打开」 is the subject, not the scaffolding.
 */
const OUR_MARKERS = /\[(?:START|END)_(?:ECHO|STEP_CONCLUSION|CODE|EXPLANATION|VERIFICATION)\]/;
const PROMPT_STEP_TALK = /第[一二三1-3]步|收尾小结|开场白/;
const PROMPT_FORM_WORDS = /开头|分区|收尾|小结|标记|结构|输出格式|输出结构|客服腔|语气|emoji/i;
/** Unambiguous on their own: no PLC requirement talks about a service-desk tone
 *  or about our card policy. */
const PROMPT_POLICY_TALK = /卡片生成策略|输出结构|输出格式|严格遵守|必须包在|不要加任何标记|客服腔/;

/**
 * The same planning, in English.
 *
 * DeepSeek thinks in Chinese on one run and in English on the next, so a filter
 * written against one form sees nothing on the other: 2026-09-09 00:40 opened
 * with "Need to produce 需求分析 with three parts. / Need to follow exact
 * structure: / Need then blank line and emoji partition analysis."
 *
 * The tell is "need" governing a word about *form*. "We need a normally closed
 * contact on the stop button" needs something of the machine and stays.
 */
const EN_PROMPT_NARRATION =
  /\bneed\b[^.]{0,40}\b(?:structure|blank\s+line|emoji|marker|section|partition|three\s+(?:parts|steps)|first\s+(?:part|step))\b/i;

function isPromptNarration(line: string): boolean {
  if (OUR_MARKERS.test(line)) return true;
  if (PROMPT_POLICY_TALK.test(line)) return true;
  if (EN_PROMPT_NARRATION.test(line)) return true;
  if (PROMPT_STEP_TALK.test(line) && PROMPT_FORM_WORDS.test(line)) return true;
  return false;
}

function isScaffolding(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) return false;

  if (isPromptNarration(trimmed)) return true;

  if (ENUM_LIST_LINE.test(trimmed)) return true;
  if (CONTEXT_HEADING_LINE.test(trimmed)) return true;
  if (BARE_SCHEMA_KEY_LINE.test(trimmed)) return true;
  if (JSON_INSTRUCTION_LINE.test(trimmed) || JSON_INSTRUCTION_LINE_ZH.test(trimmed)) return true;

  // Two or more of our field names in one line is the schema being recited,
  // not a thought about the machine. One is a plausible coincidence.
  if (countSchemaFieldNames(trimmed) >= 2) return true;

  return false;
}

/**
 * The section the analysis prompt asks for first. Exactly one per report.
 */
const FIRST_SECTION_LEAD = /(?:^|\n)\s*(?:\*\*)?\s*\u{2705}/gu;

/**
 * The last draft, when the model wrote more than one.
 *
 * Every filter above matches on vocabulary, and vocabulary is a losing game:
 * the filter grew markers, then policy words, then step words, then English
 * narration, and on 2026-09-09 a changed prompt produced nine fresh lines of
 * self-audit — 「注意：emoji标题要符合…」、「所以全部覆盖，待确认可不写」、「我们开始
 * 构造回答。」 — of which it caught none. Each round of leakage speaks a
 * different dialect, so a rule written against the last one cannot see the next.
 *
 * Position survives rewording. The report has exactly one ✅ 已明确 by
 * construction; a stream carrying three is a model rehearsing, and only the
 * last attempt is the answer. Nothing is matched on what it says, so a new
 * dialect of deliberation is dropped along with the draft it sits in.
 *
 * Conservative where it cannot tell: one lead means one draft, and the text is
 * returned untouched.
 */
export function keepLastAnalysisDraft(text: string): string {
  if (!text) return text;

  const leads = [...text.matchAll(FIRST_SECTION_LEAD)];
  if (leads.length < 2) return text;

  const last = leads[leads.length - 1];
  return text.slice(last.index ?? 0).replace(/^\n+/, '');
}

/**
 * Drop our own scaffolding from a block of model reasoning, leaving the
 * reasoning itself untouched. Runs of blank lines left behind are collapsed so
 * removals do not open holes in the text.
 */
const EDIT_BLOCK_OPEN = /^\s*<{7}\s*SEARCH\s*$/;
const EDIT_BLOCK_CLOSE = /^\s*>{7}\s*REPLACE\s*$/;

export function stripInternalScaffolding(text: string): string {
  if (!text) return text;

  const kept: string[] = [];
  // A SEARCH/REPLACE edit block is program, not reasoning. The runtime no
  // longer forwards one as prose; this keeps the ones it forwarded before that
  // change out of reasoning already stored in the conversation.
  let inEditBlock = false;
  for (const line of text.split('\n')) {
    if (inEditBlock) {
      if (EDIT_BLOCK_CLOSE.test(line)) inEditBlock = false;
      continue;
    }
    if (EDIT_BLOCK_OPEN.test(line)) {
      inEditBlock = true;
      continue;
    }
    if (isScaffolding(line)) continue;
    // Never leave more than one blank line where something was removed.
    if (!line.trim() && !kept[kept.length - 1]?.trim() && kept.length > 0) continue;
    kept.push(line);
  }

  while (kept.length > 0 && !kept[0].trim()) kept.shift();
  while (kept.length > 0 && !kept[kept.length - 1].trim()) kept.pop();

  return kept.join('\n');
}
